import React, { ReactNode, useEffect, useMemo } from 'react';
import MuiSelect, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import { Icon } from '../../Icon';

import './styles.scss';

export interface SelectOption {
    value: string;
    text: string;
    additionalText?: string;
    icon?: ReactNode;
}

interface Props {
    id: string;
    value: string;
    defaultValue?: string;
    options: SelectOption[];
    popularOptions?: SelectOption[];
    className?: string;
    dropdownClassname?: string;
    removeBorderRadius?: 'top' | 'bottom';
    onChange: (id: string, value: unknown) => void;
    popularOptionsTitle?: string;
    allOptionsTitle?: string;
}

export const Select = ({
    id,
    value,
    defaultValue,
    className = '',
    dropdownClassname = '',
    options,
    popularOptions,
    removeBorderRadius,
    popularOptionsTitle,
    allOptionsTitle,
    onChange,
}: Props) => {
    useEffect(() => {
        if (defaultValue) {
            onChange(id, defaultValue);
        }
    }, [defaultValue]);

    const renderOptions = useMemo(() => {
        let result = [];

        const allOptions = options.map((o) => (
            <MenuItem value={o.value} key={o.value} className='app-select__option'>
                <div className='app-select__option-value'>
                    {o.icon ? o.icon : null}
                    <span>{o.text}</span>
                    {o.additionalText ? (
                        <span className='app-select__option-additional-text'>{o.additionalText}</span>
                    ) : null}
                </div>
            </MenuItem>
        ));

        if (popularOptionsTitle) {
            result.push(
                <ListSubheader className='app-select__category-subtitle' key='popular-title'>
                    {popularOptionsTitle}
                </ListSubheader>,
            );
        }

        if (popularOptions && popularOptions.length) {
            result = [
                ...result,
                ...popularOptions.map((o) => (
                    <MenuItem
                        value={o.value}
                        key={`${o.value}-popular`}
                        className='app-select__option app-select__option--popular'
                    >
                        <div className='app-select__option-value'>
                            {o.icon ? o.icon : null}
                            <span>{o.text}</span>
                            {o.additionalText ? (
                                <span className='app-select__option-additional-text'>{o.additionalText}</span>
                            ) : null}
                        </div>
                    </MenuItem>
                )),
            ];
        }

        if (allOptionsTitle) {
            result.push(
                <ListSubheader
                    className='app-select__category-subtitle app-select__category-subtitle--with-margin'
                    key='all-title'
                >
                    {allOptionsTitle}
                </ListSubheader>,
            );
        }

        result = [...result, ...allOptions];

        return result;
    }, [options, popularOptions, popularOptionsTitle, allOptionsTitle]);

    const onChangeHandler = (e: SelectChangeEvent) => {
        onChange(id, e.target.value);
    };

    const borderRadiusClassName = useMemo(() => {
        switch (removeBorderRadius) {
            case 'top':
                return 'app-select--no-top-border';
            case 'bottom':
                return 'app-select--no-bottom-border';
            default:
                return '';
        }
    }, [removeBorderRadius]);

    return (
        <div className={`app-select ${className} ${borderRadiusClassName}`}>
            <MuiSelect
                id={id}
                value={value}
                className='app-select__mui-select'
                onChange={onChangeHandler}
                IconComponent={(props) => <Icon icon='chevron-down' color='info' {...props} />}
                sx={{
                    boxShadow: 0,
                }}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    classes: { paper: `app-select__dropdown ${dropdownClassname}`, list: 'app-select__list' },
                }}
            >
                {renderOptions}
            </MuiSelect>
        </div>
    );
};
